import { useEffect, useState } from 'react';
import { Card, Switch } from 'antd';
import { FaPlusCircle } from '@react-icons/all-files/fa/FaPlusCircle';
import { getPriceText } from '@zorba-shared/core/Services/textService';
import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuid } from 'uuid';
import { setModalState } from '../../../../reducers/Modal/actions';
import { MODAL_CREATE_WATCHLIST } from '../../../../reducers/Modal/types';
import { useGetListDetails } from '../../../../queries/Watchlist';
import { ZorbaButton, ZorbaSelect } from '../../../../components/forms';
import { MyLists } from './Components/MyLists';
import { EntitiesTable } from './Components/EntitiesTable';
import { rangeOptions } from '../../../TopBuyersReportPage/constants';
import { BUTTON_VARIANT_DARK } from '../../../../components/forms/ZorbaButton/constants';
import { Spinner } from '../../../../components/layout';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../services/notificationsService';
import { postRequest } from '../../../../services/requestService';

import './index.scss';

export const Watchlist = ({
  selectedList,
  setSelectedList,
  refetchWatchlists,
}) => {
  const { userDetails } = useSelector(({ AuthReducer }) => AuthReducer);
  const [selectedMonths, setSelectedMonths] = useState(3);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [
    notificationsAcqusitionDecreaseEnabled,
    setNotificationsAcqusitionDecreaseEnabled,
  ] = useState(false);
  const [notificationsRecipients, setNotificationsRecipients] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const isZorbaUser = userDetails?.email?.includes('@getzorba.com');

  useEffect(() => {
    if (selectedList) {
      setNotificationsEnabled(selectedList.notificationsEnabled);
      setNotificationsRecipients(selectedList.notificationsRecipients);
      setNotificationsAcqusitionDecreaseEnabled(
        selectedList.notificationsAcqusitionDecreaseEnabled,
      );
    }
  }, [selectedList]);

  const dispatch = useDispatch();
  const openCreateWatchlistModal = () => {
    dispatch(setModalState(MODAL_CREATE_WATCHLIST, true));
  };
  const { appliedFilters } = useSelector(
    ({ FiltersReducer }) => FiltersReducer,
  );

  const handleRangeChange = (value) => {
    setSelectedMonths(value);
  };

  const { data, isLoading: isDetailsLoading, isRefetching } = useGetListDetails(
    {
      listId: selectedList?._id,
      selectedMonthRange: selectedMonths,
      appliedFilters,
    },
  );

  const handleSave = async () => {
    setIsPending(true);
    try {
      await postRequest('/data/watchlist/manage-notifications', {
        listId: selectedList._id,
        notificationsEnabled,
        notificationsRecipients,
        notificationsAcqusitionDecreaseEnabled,
      });
      refetchWatchlists();
      showSuccessToast('Notification preferences saved successfully.');
    } catch (error) {
      showErrorToast(
        'Failed to save notification preferences. Please try again.',
      );
    }
    setIsPending(false);
  };

  return (
    <div className="watchlist-page">
      <div className="range-container">
        {rangeOptions.map((option) => (
          <Card
            key={uuid()}
            className={`range-card ${
              selectedMonths === option.value ? 'active' : ''
            }`}
            onClick={() => handleRangeChange(option.value)}
          >
            {option.label}
          </Card>
        ))}
      </div>
      {!isDetailsLoading && !isRefetching && (
        <div className="stats">
          <Card>
            <h3>Accounts</h3>
            <h1>{data?.accounts || 0}</h1>
          </Card>
          <Card>
            <h3>Total Acquisitions</h3>
            <h1>{data?.totalAcquisitions || 0}</h1>
          </Card>
          <Card>
            <h3>Total Dispositions</h3>
            <h1>{data?.totalDispositions || 0}</h1>
          </Card>
          <Card>
            <h3>Total Acquisitions GMV</h3>
            <h1>{getPriceText(data?.totalAcquisitionsGMV || 0)}</h1>
          </Card>
        </div>
      )}
      <div className="watchlist-content-container">
        <Card
          title={
            <div className="my-lists-title">
              My lists{' '}
              <FaPlusCircle
                size={25}
                className="plus-icon"
                onClick={openCreateWatchlistModal}
              />
            </div>
          }
          className="my-lists-card"
        >
          <MyLists
            setSelectedList={setSelectedList}
            selectedList={selectedList}
          />
        </Card>
        <div className="settings-accounts-container">
          {isZorbaUser && (
            <Card
              title="Settings"
              className="notifications-card"
              extra="Set up email alerts to get notified when the accounts you monitor change their behavior."
            >
              <div className="card-content-container">
                {isPending ? (
                  <Spinner />
                ) : (
                  <>
                    <div className="switch-container">
                      <span className="switch-label">
                        Notify on transaction increase
                      </span>
                      <Switch
                        checked={notificationsEnabled}
                        onChange={(newVal) => {
                          setNotificationsEnabled(newVal);
                        }}
                      />
                    </div>
                    <div className="switch-container">
                      <span className="switch-label">
                        Notify on pause acquisitions
                      </span>
                      <Switch
                        checked={notificationsAcqusitionDecreaseEnabled}
                        onChange={(newVal) => {
                          setNotificationsAcqusitionDecreaseEnabled(newVal);
                        }}
                      />
                    </div>
                    <div className="select-container">
                      <ZorbaSelect
                        label="Email recipients"
                        mode="tags"
                        onChange={(e) => {
                          setNotificationsRecipients(e);
                        }}
                        value={notificationsRecipients}
                      />
                    </div>
                  </>
                )}
                <div>
                  <ZorbaButton
                    fullWidth={false}
                    variant={BUTTON_VARIANT_DARK}
                    isLoading={isPending}
                    disabled={
                      isPending ||
                      (notificationsEnabled ===
                        selectedList?.notificationsEnabled &&
                        notificationsAcqusitionDecreaseEnabled ===
                          selectedList?.notificationsAcqusitionDecreaseEnabled &&
                        notificationsRecipients?.length ===
                          selectedList?.notificationsRecipients?.length &&
                        notificationsRecipients?.every((recipient) =>
                          selectedList?.notificationsRecipients?.includes(
                            recipient,
                          ),
                        ))
                    }
                    onClick={handleSave}
                  >
                    Save
                  </ZorbaButton>
                </div>
              </div>
            </Card>
          )}
          <Card title="Accounts" className="accounts-card">
            <EntitiesTable
              selectedList={selectedList}
              data={data}
              isLoading={isDetailsLoading || isRefetching}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};
